import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Checkbox,
    TextField,
    IconButton,
    Toolbar,
    Typography,
    Tooltip,
    Paper,
    Menu,
    MenuItem
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterDrawer from './features/filters/FilterDrawer';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableSkeleton from './components/TableSkeleton';

/**
 * DynamicTable
 *
 * Reusable data table with:
 *  - Sticky header
 *  - Optional actions column (sticky at right)
 *  - Row selection (single / multi)
 *  - Client-side text filtering (global)
 *  - Pagination (client-side)
 *
 * For large datasets server-side pagination & filtering can be integrated by
 * controlling `page`, `rowsPerPage`, `filter` externally and passing handlers.
 */
export const DynamicTable = ({
    columns,
    rows,
    getRowId = (row, idx) => row.id ?? idx,
    actionsRenderer, // (row) => [{label,onClick}]
    onSelectionChange,
    initialRowsPerPage = 25,
    rowsPerPageOptions = [10, 25, 50, 100],
    stickyHeader = true,
    enableSelection = true,
    enableGlobalFilter = true,
    initialFilter = '',
    dense = false,
    // server side pagination support
    serverPagination = false,
    page: extPage,
    rowsPerPage: extRowsPerPage,
    totalCount,
    onPageChange: extOnPageChange,
    onRowsPerPageChange: extOnRowsPerPageChange,
    filterConfig = [],
    filters = {},
    onFiltersChange,
    showFilterButton = true,
    defaultActiveFilterKeys = [],
    // loading state
    isLoading = false,
    skeletonRows = 5,
}) => {
    const [intPage, setIntPage] = useState(0);
    const [intRowsPerPage, setIntRowsPerPage] = useState(initialRowsPerPage);
    // Selection state stored as a map { [rowId]: true } for persistence across pages
    const [selectedMap, setSelectedMap] = useState({});
    const [filter, setFilter] = useState(initialFilter);
    const [anchorEls, setAnchorEls] = useState({}); // per-row action menu anchor
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [draftFilters, setDraftFilters] = useState(filters);
    const [activeFilterKeys, setActiveFilterKeys] = useState(defaultActiveFilterKeys.length ? defaultActiveFilterKeys : []);

    const page = serverPagination ? extPage : intPage;
    const rowsPerPage = serverPagination ? extRowsPerPage : intRowsPerPage;

    useEffect(() => {
        if (onSelectionChange) {
            const ids = Object.keys(selectedMap).filter(k => selectedMap[k]);
            onSelectionChange(ids);
        }
    }, [selectedMap, onSelectionChange]);

    useEffect(() => { setDraftFilters(filters); }, [filters]);

    const handleSelectAll = (e) => {
        if (!enableSelection) return;
        if (e.target.checked) {
            // Add all currently filtered rows to map (retain existing others)
            setSelectedMap(prev => {
                const next = { ...prev };
                filteredRows.forEach((r, idx) => { next[getRowId(r, idx)] = true; });
                return next;
            });
        } else {
            // Remove only the currently filtered rows from map
            setSelectedMap(prev => {
                const next = { ...prev };
                filteredRows.forEach((r, idx) => { delete next[getRowId(r, idx)]; });
                return next;
            });
        }
    };

    const toggleRow = (row, idx) => {
        if (!enableSelection) return;
        const id = getRowId(row, idx);
        setSelectedMap(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const isSelected = (row, idx) => !!selectedMap[getRowId(row, idx)];

    const filteredRows = useMemo(() => {
        if (!enableGlobalFilter || !filter) return rows || [];
        const lowered = filter.toLowerCase();
        return (rows || []).filter(r => Object.values(r || {}).some(v => String(v ?? '').toLowerCase().includes(lowered)));
    }, [rows, filter, enableGlobalFilter]);

    const pagedRows = useMemo(() => {
        if (serverPagination) return filteredRows; // assume rows already a single page
        const start = page * rowsPerPage;
        return filteredRows.slice(start, start + rowsPerPage);
    }, [filteredRows, page, rowsPerPage, serverPagination]);

    const handleChangePage = (evt, newPage) => {
        if (serverPagination) extOnPageChange && extOnPageChange(newPage); else setIntPage(newPage);
    };
    const handleChangeRowsPerPage = (e) => {
        const val = parseInt(e.target.value, 10);
        if (serverPagination) {
            extOnRowsPerPageChange && extOnRowsPerPageChange(val);
        } else {
            setIntRowsPerPage(val);
            setIntPage(0);
        }
    };

    const openMenu = (rowId, evt) => {
        setAnchorEls(prev => ({ ...prev, [rowId]: evt.currentTarget }));
    };
    const closeMenu = (rowId) => {
        setAnchorEls(prev => ({ ...prev, [rowId]: null }));
    };

    const effectiveCount = serverPagination && typeof totalCount === 'number' ? totalCount : filteredRows.length;
    const selectedIds = useMemo(() => Object.keys(selectedMap).filter(k => selectedMap[k]), [selectedMap]);
    const currentPageSelectedCount = useMemo(() => pagedRows.reduce((acc, r, i) => acc + (isSelected(r, serverPagination ? i : i + page * rowsPerPage) ? 1 : 0), 0), [pagedRows, page, rowsPerPage, serverPagination, isSelected]);
    const allFilteredSelected = filteredRows.length > 0 && filteredRows.every((r, idx) => selectedMap[getRowId(r, idx)]);
    const someFilteredSelected = filteredRows.some((r, idx) => selectedMap[getRowId(r, idx)]) && !allFilteredSelected;

    return (
        <Paper sx={{ width: '100%', overflow: 'auto', position: 'relative' }}>
            <Toolbar sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                    {/* <Typography variant="h6" sx={{ flexGrow: 1 }}>Results</Typography> */}
                </Box>
                {showFilterButton && filterConfig.length > 0 && (
                    <Tooltip title="Filters">
                        <IconButton onClick={() => { setDraftFilters(filters); setFilterDrawerOpen(true); }}>
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {enableGlobalFilter && (
                    <TextField
                        size="small"
                        label="Search"
                        value={filter}
                        onChange={(e) => { setFilter(e.target.value); if (!serverPagination) setIntPage(0); }}
                    />
                )}
            </Toolbar>
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader={stickyHeader} size={dense ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow>
                            {enableSelection && (
                                <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, zIndex: 3, background: 'white' }}>
                                    <Checkbox
                                        indeterminate={someFilteredSelected}
                                        checked={allFilteredSelected}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                            )}
                            {columns.map(col => (
                                <TableCell key={col.id} sx={{ background: 'white', top: 0 }} align={col.align || 'left'} width={col.width}>{col.label}</TableCell>
                            ))}
                            {actionsRenderer && (
                                <TableCell sx={{ position: 'sticky', right: 0, zIndex: 3, background: 'white' }}>Actions</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableSkeleton
                                columns={columns}
                                rowCount={skeletonRows}
                                enableSelection={enableSelection}
                                actionsRenderer={actionsRenderer}
                            />
                        ) : (
                            <>
                                {pagedRows.map((row, rIdx) => {
                                    const absoluteIdx = serverPagination ? rIdx : rIdx + page * rowsPerPage;
                                    const id = getRowId(row, absoluteIdx);
                                    const selectedRow = isSelected(row, absoluteIdx);
                                    return (
                                        <TableRow
                                            hover
                                            key={id}
                                            selected={selectedRow}
                                            onClick={() => toggleRow(row, absoluteIdx)}
                                            sx={{ cursor: enableSelection ? 'pointer' : 'default' }}
                                        >
                                            {enableSelection && (
                                                <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                                                    <Checkbox checked={selectedRow} />
                                                </TableCell>
                                            )}
                                            {columns.map(col => (
                                                <TableCell key={col.id} align={col.align || 'left'}>
                                                    {col.dynamicRender ? col.dynamicRender(row) : (col.render ? col.render(row[col.accessor], row) : row[col.accessor])}
                                                </TableCell>
                                            ))}
                                            {actionsRenderer && (
                                                <TableCell sx={{ position: 'sticky', right: 0, background: 'white', zIndex: 1 }} onClick={(e) => e.stopPropagation()}>
                                                    <IconButton size="small" onClick={(e) => openMenu(id, e)}>
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        open={Boolean(anchorEls[id])}
                                                        anchorEl={anchorEls[id]}
                                                        onClose={() => closeMenu(id)}
                                                    >
                                                        {actionsRenderer(row).map((act, i) => (
                                                            <MenuItem key={i} onClick={() => { act.onClick(row); closeMenu(id); }}>
                                                                {act.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Menu>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                                {!isLoading && pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={(columns.length + (enableSelection ? 1 : 0) + (actionsRenderer ? 1 : 0))}>
                                            <Typography variant="body2" align="center">No records found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {enableSelection && selectedIds.length > 0 && (
                <Box px={2} py={1} display="flex" alignItems="center" justifyContent="space-between" sx={{ borderTop: '1px solid #eee', background: '#fafafa' }}>
                    <Typography variant="body2">
                        Selected: {selectedIds.length} total{currentPageSelectedCount !== selectedIds.length ? ` (this page: ${currentPageSelectedCount})` : ''}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Tooltip title="Clear selection">
                            <IconButton size="small" onClick={() => setSelectedMap({})}>✕</IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}
            <TablePagination
                component="div"
                count={effectiveCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
            />
            <FilterDrawer
                open={filterDrawerOpen}
                onClose={() => { setFilterDrawerOpen(false); setDraftFilters(filters); }}
                filterConfig={filterConfig}
                values={draftFilters}
                onChange={(vals) => setDraftFilters(vals)}
                onApply={(vals) => { onFiltersChange && onFiltersChange(vals); }}
                onReset={() => { setDraftFilters({}); onFiltersChange && onFiltersChange({}); }}
                activeKeys={activeFilterKeys}
                onActiveKeysChange={(keys) => setActiveFilterKeys(keys)}
            />
        </Paper>
    );
};

DynamicTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        accessor: PropTypes.string.isRequired,
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        align: PropTypes.oneOf(['left', 'right', 'center']),
        render: PropTypes.func,
    })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object),
    getRowId: PropTypes.func,
    actionsRenderer: PropTypes.func,
    onSelectionChange: PropTypes.func,
    initialRowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    stickyHeader: PropTypes.bool,
    enableSelection: PropTypes.bool,
    enableGlobalFilter: PropTypes.bool,
    initialFilter: PropTypes.string,
    dense: PropTypes.bool,
    serverPagination: PropTypes.bool,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    totalCount: PropTypes.number,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    filterConfig: PropTypes.array,
    filters: PropTypes.object,
    onFiltersChange: PropTypes.func,
    showFilterButton: PropTypes.bool,
    defaultActiveFilterKeys: PropTypes.arrayOf(PropTypes.string),
    isLoading: PropTypes.bool,
    skeletonRows: PropTypes.number,
};

export default DynamicTable;
